import { GetStaticPropsContext } from 'next';
import React, { useEffect } from 'react';
import { BlogPostHero } from '../../components/blogPostHero/blogPostHero.comp';
import { Layout } from '../../components/layout/layout.comp';
import { IBlogPage } from '../../types';
import { getDataFromDB } from '../../lib/mongodb';
import { BN_ARTICLE_SLUGS } from '../../lib/bnArticleSlugs';
import { useRouter } from 'next/router';
import { Faq } from '../../components/faq/faq.comp';
import { JsonLd } from '../../components/jsonLd/jsonLd.comp';
import { buildArticle, buildBreadcrumbs } from '../../lib/schema';

const BN_BLOG_QUERY = { status: 'published', slug: { $in: BN_ARTICLE_SLUGS } };

export default function BlogPost({ blogPost, suggestions, redirect }: { blogPost: IBlogPage, suggestions: IBlogPage[]; redirect?: { destination: string, permanent: boolean; }; }) {
    const router = useRouter();

    useEffect(() => {
        // Redirect if necessary
        if (redirect) {
            router.replace(redirect.destination);
        }
        // Redirect to 404 page if blogPost is not found
        else if (!blogPost) {
            router.replace('/404');
        }
    }, [blogPost, redirect, router]);

    if (!blogPost) {
        return <></>;
    }

    if (!blogPost) {
        return <></>;
    }

    return (
        <Layout
            isDark
            showFooter={true}
            showHeader={true}
            title={blogPost.meta.title || blogPost.name || ''}
            description={blogPost.meta.description || blogPost.excerpt || ''}
            image={blogPost.meta.imgUrl || blogPost.coverImage || ''}
            url={`https://www.bracketsninja.com/blog/${blogPost.slug}`}
        >
            <LoadScripts blog={blogPost} />
            <BlogPostHero suggestions={suggestions} blogPost={blogPost} />
            {blogPost.faq.length > 2 &&
                // Because omicron default faq field was open (not anymore) 
                <Faq
                    faqs={blogPost.faq}
                />
            }
        </Layout>
    );
};

// Article + breadcrumbs, server-rendered so a crawler actually receives them.
// This previously went out through next/script, which injects on the client,
// so none of it reached the served HTML.
const LoadScripts = ({ blog }: { blog: IBlogPage; }) => {
    const article = buildArticle(blog);
    // Prefer the shorter meta title for the trail. A breadcrumb is a label,
    // not a headline, and the stored name often carries a subtitle after a colon.
    const crumbLabel = blog.meta?.title || blog.name;
    const breadcrumbs = buildBreadcrumbs(`blog/${blog.slug}`, crumbLabel);

    return (
        <>
            {article && <JsonLd data={article} />}
            {breadcrumbs && <JsonLd data={breadcrumbs} />}
        </>
    );
};

export async function getStaticPaths() {
    try {
        const allPosts = await getDataFromDB<IBlogPage>('blogs', BN_BLOG_QUERY, {
            slug: 1,
            oldSlugs: 1,
            name: 1,
            excerpt: 1,
            coverImage: 1,
            author: 1,
            publishedDate: 1,
            categories: 1
        });

        const paths = allPosts.reduce((acc: any, blog) => {
            const cleanSlug = (blog.slug || '').trim();
            if (cleanSlug) {
                acc.push({ params: { slug: cleanSlug } });
            }
            if (Array.isArray(blog.oldSlugs) && blog.oldSlugs.length > 0) {
                blog.oldSlugs.forEach((oldSlug: string) => {
                    // Guard against null/whitespace oldSlugs (a stray trailing space
                    // was generating a phantom duplicate page in the sitemap).
                    const cleanOldSlug = (oldSlug || '').trim();
                    if (cleanOldSlug && cleanOldSlug !== cleanSlug) {
                        acc.push({ params: { slug: cleanOldSlug } });
                    }
                });
            }
            return acc;
        }, []);

        return { paths, fallback: true };
    } catch (e) {
        console.error(e);
        return { paths: [], fallback: false };
    }
}

export async function getStaticProps({ params }: GetStaticPropsContext<{ slug: string; }>) {
    const allPosts: IBlogPage[] = await getDataFromDB<IBlogPage>('blogs', BN_BLOG_QUERY);
    const blogPost = allPosts.find((b) => b.slug === params?.slug || '');

    if (blogPost) {
        const suggestions = getPostSuggestions(blogPost, allPosts);
        return { props: { blogPost, suggestions } };
    }

    // If the post is not found using the slug, check the old slugs
    const postWithOldSlug = allPosts.find((post) => Array.isArray(post.oldSlugs) && post.oldSlugs.includes(params?.slug || ''));
    if (postWithOldSlug) {
        // Redirect to the new slug
        return {
            props: {
                redirect: {
                    destination: `/blog/${postWithOldSlug.slug}`,
                    permanent: true,
                },
            },
        };
    }

    // If the post is not found using the newSlug field either, return a not found page
    return { notFound: true };
}

function getPostSuggestions(blogPost: IBlogPage, allPosts: IBlogPage[]) {
    const currentPostCategories = (blogPost.categories || []).map(
        (cat) => cat.name
    );

    const randomPosts = allPosts.sort(() => Math.random() - 0.5);
    let blogPostSuggestions = randomPosts
        .filter((post: IBlogPage) => {
            const postCategories = post.categories.map((cat) => cat.name);
            if (postCategories.map((cat) => currentPostCategories.includes(cat))) {
                if (post.name !== blogPost.name) return post;
            }
        })
        .slice(0, 1);

    if (blogPostSuggestions.length === 1) {
        const goodSuggestedPost = blogPostSuggestions[0];
        blogPostSuggestions = randomPosts
            .filter((post: IBlogPage) => {
                if (
                    post.name !== blogPost.name &&
                    post.name !== goodSuggestedPost.name
                )
                    return post;
            })
            .slice(0, 2);
        blogPostSuggestions.push(goodSuggestedPost);
    }

    if (blogPostSuggestions.length === 0) {
        blogPostSuggestions = randomPosts
            .filter((post: IBlogPage) => {
                if (post.name !== blogPost.name) return post;
            })
            .slice(0, 3);
    }

    return blogPostSuggestions;
}